import { type Dispatch, type FormEvent, type SetStateAction, useState } from 'react';
import ActivateBuild from '../../components/flow/ActivateBuild';
import {
    type McrDeployResult,
    connectToEngineSocket,
    disconnectFromEngineSocket,
} from '../../mcr/signalRConnection';
import { getDevelopmentEnvironment } from '../../sources/environments';
import { deploy, notifyRuntimesOnDeploy } from '../../sources/releases';
import { buildTheme } from '../../sources/theme';
import type { TenantRuntime } from '../../types/Tenant';
import type { ThemeBuildRequestAPI } from '../../types/theme';
import { isNullOrUndefined } from '../../utils/guard';
import { guid } from '../../utils/guid';
import { EnvironmentsProvider } from '../admin/environments/EnvironmentsProvider';
import GenericModal from '../generic/modal/GenericModal';
import Loader from '../loader/Loader';
import { useNotificationDispatch } from '../notifications/NotificationProvider';

interface Props {
    themeId: string;
    showBuildModal: boolean;
    setShowBuildModal: Dispatch<SetStateAction<boolean>>;
    container?: HTMLDivElement | undefined;
    callback: () => void;
    runtimes: TenantRuntime[] | null;
}

const BuildThemeModal = ({
    themeId,
    showBuildModal,
    setShowBuildModal,
    container,
    callback,
    runtimes,
}: Props) => {
    const [releaseName, setReleaseName] = useState<string | null>(null);
    const [releaseId, setReleaseId] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const [building, setBuilding] = useState<boolean>(false);

    const dispatchNotification = useNotificationDispatch();

    const closeModal = () => setShowBuildModal(false);

    const sendBuildThemeRequest = async () => {
        setBuilding(true);

        const request: ThemeBuildRequestAPI = {
            themeIds: [themeId],
            releaseName,
            comment,
            releaseId,
        };

        const returnedReleaseId = await buildTheme(request);
        setReleaseId(returnedReleaseId);
        const devEnvironment = await getDevelopmentEnvironment();

        if (returnedReleaseId) {
            if (runtimes && runtimes?.length > 0) {
                const messageId: string = guid();
                const publishPromise = connectToEngineSocket();

                await notifyRuntimesOnDeploy(
                    messageId,
                    returnedReleaseId,
                    devEnvironment.id,
                    false,
                );

                let deployResult: McrDeployResult | null = null;

                try {
                    deployResult = await publishPromise;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                } finally {
                    if (deployResult?.success) {
                        closeModal();
                        callback();
                    }
                    await disconnectFromEngineSocket();
                    setBuilding(false);
                }
            } else {
                await deploy(returnedReleaseId, devEnvironment.id);
                setBuilding(false);
                closeModal();
                callback();
            }
        }
    };

    const onCommentChanged = ({ currentTarget: { value } }: FormEvent<HTMLTextAreaElement>) =>
        setComment(value);

    const onReleaseIdChanged = (id: string | null) => {
        setReleaseId(id);
    };

    const onReleaseNameChanged = (name: string | null) => {
        setReleaseName(name);
    };

    return (
        <GenericModal
            container={container}
            title="Build Theme"
            onHide={closeModal}
            show={showBuildModal}
            renderBody={() =>
                building ? (
                    <Loader message="Building theme" />
                ) : (
                    <EnvironmentsProvider>
                        <ActivateBuild
                            isThemeBuild={true}
                            onReleaseIdChanged={onReleaseIdChanged}
                            onReleaseNameChanged={onReleaseNameChanged}
                            onCommentChanged={onCommentChanged}
                        />
                    </EnvironmentsProvider>
                )
            }
            renderFooter={() => (
                <>
                    <button
                        type="button"
                        onClick={closeModal}
                        className="btn btn-default"
                        disabled={building}
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        onClick={sendBuildThemeRequest}
                        className="btn btn-primary"
                        disabled={
                            (isNullOrUndefined(releaseId) && isNullOrUndefined(releaseName)) ||
                            building
                        }
                    >
                        Build Theme
                    </button>
                </>
            )}
        />
    );
};

export default BuildThemeModal;
